import { Spin } from "./Spin";
import { forwardRef, useState, type CSSProperties, type MouseEvent } from "react";

import { cx, useKitDisabled } from "./fieldContext";

export interface SwitchProps {
  /** Zustand; in `Form.Item valuePropName="checked"` setzt das Formular ihn. */
  checked?: boolean;
  defaultChecked?: boolean;
  value?: boolean;
  onChange?: (checked: boolean, event: MouseEvent<HTMLButtonElement>) => void;
  onClick?: (checked: boolean, event: MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  loading?: boolean;
  id?: string;
  title?: string;
  className?: string;
  style?: CSSProperties;
  autoFocus?: boolean;
  tabIndex?: number;
  "aria-label"?: string;
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
}

/**
 * Schalter des Kits (`role="switch"`, Ersatz für Antd `Switch`). Bewusst ohne Größe und ohne Text im Schalter
 * (14.10.2026): alle Schalter sehen gleich aus, die Bedeutung steht im Feld- oder Spaltenlabel daneben.
 */
export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  { checked, defaultChecked, value, onChange, onClick, disabled, loading, className, ...rest },
  ref,
) {
  const [inner, setInner] = useState(Boolean(defaultChecked));
  const controlled = checked !== undefined || value !== undefined;
  const isOn = controlled ? Boolean(checked ?? value) : inner;
  const mergedDisabled = useKitDisabled(disabled);
  return (
    <button
      ref={ref}
      type="button"
      role="switch"
      aria-checked={isOn}
      aria-busy={loading || undefined}
      disabled={mergedDisabled || loading}
      className={cx("ck-switch", isOn && "is-on", className)}
      onClick={(event) => {
        const next = !isOn;
        if (!controlled) setInner(next);
        onChange?.(next, event);
        return onClick?.(next, event);
      }}
      {...rest}
    >
      <span className="ck-switch-thumb" aria-hidden="true">{loading ? <Spin inline /> : null}</span>
    </button>
  );
});
